import {
  ModalityType,
  type ApiToolCallModel,
  type Modality,
} from '@littlebird/sdk';

import type { LocalTerminalExecResult } from '@/features/electron-api/features/LocalTerminalAPI';

export const TERMINAL_RUN_TOOL_SLUG = 'TERMINAL_RUN';

/** The `tool_execute` step for an agent-run shell command (device `terminal` toolkit). */
/**
 * Is this step a Terminal run? The slug alone would also match a same-named
 * tool from another toolkit, so when the modality names its integration it
 * has to be ours. The field is optional on the wire (absent in OAuth connect
 * mode and on some older steps), so its absence is not held against the step.
 */
export const isTerminalRunModality = (modality: Modality): boolean =>
  modality.type === ModalityType.ToolExecute &&
  modality.tool_slug === TERMINAL_RUN_TOOL_SLUG &&
  (modality.integration_name === undefined ||
    isTerminalIntegrationName(modality.integration_name));

/** "terminal", "device:terminal" or "device:terminal@<device>". */
const isTerminalIntegrationName = (name: string): boolean =>
  name === 'terminal' ||
  name === 'device:terminal' ||
  name.startsWith('device:terminal@');

export interface TerminalRunArgs {
  command: string;
  why?: string;
  cwd?: string;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null;

const parseJson = (raw: unknown): unknown => {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/**
 * The command the agent asked for. Prefers the tool call's arguments; falls back
 * to the modality's serialized `arguments`. Tolerates the `tool_execute` wrapper
 * shape (`{tool_slug, arguments: "<json>"}`) as well as the bare tool input.
 */
export function parseTerminalRunArgs(
  modality: Modality,
  toolCall?: ApiToolCallModel
): TerminalRunArgs | null {
  const candidates: unknown[] = [toolCall?.arguments];
  if (modality.type === ModalityType.ToolExecute) {
    candidates.push(parseJson(modality.arguments));
  }
  for (const candidate of candidates) {
    let args = asRecord(candidate);
    if (args && typeof args.command !== 'string' && 'arguments' in args) {
      args = asRecord(parseJson(args.arguments));
    }
    if (!args || typeof args.command !== 'string') continue;
    const command = args.command.trim();
    if (!command) continue;
    const why = typeof args.why === 'string' ? args.why.trim() : '';
    return {
      command,
      why: why || undefined,
      cwd: typeof args.cwd === 'string' ? args.cwd : undefined,
    };
  }
  return null;
}

/**
 * What the agent sent when `parseTerminalRunArgs` could not make a command of
 * it, so the card can still show *something* instead of a bare "Used Terminal".
 * Prefers the inner `arguments` string of the `tool_execute` wrapper (that is
 * where malformed JSON from the model ends up); else the whole payload.
 */
export function rawTerminalRunArgs(
  modality: Modality,
  toolCall?: ApiToolCallModel
): string | null {
  const candidates: unknown[] = [toolCall?.arguments];
  if (modality.type === ModalityType.ToolExecute) {
    candidates.push(modality.arguments);
  }
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const record = asRecord(candidate);
    const inner = record?.arguments;
    const text =
      typeof inner === 'string'
        ? inner
        : typeof candidate === 'string'
          ? candidate
          : JSON.stringify(candidate);
    if (text && text.trim()) return text.trim();
  }
  return null;
}

/**
 * The exec result stored on a finished tool call. The toolkit returns
 * `{ output: <result> }`, and the transport may serialize either level, so
 * accept both.
 */
export function parseExecResult(
  raw: string | null | undefined
): LocalTerminalExecResult | null {
  let value = asRecord(parseJson(raw));
  if (value && typeof value.sessionId !== 'string' && 'output' in value) {
    value = asRecord(parseJson(value.output));
  }
  if (!value || typeof value.sessionId !== 'string' || !('exitCode' in value)) {
    return null;
  }
  // A synced result crossed the network; shape it rather than trust it. The
  // identity fields above decide whether this is an exec result at all, the
  // rest are defaulted so a missing or mistyped field degrades the card
  // instead of throwing inside a store action.
  return {
    sessionId: value.sessionId,
    command: str(value.command),
    cwd: str(value.cwd),
    shell: str(value.shell),
    exitCode: typeof value.exitCode === 'number' ? value.exitCode : null,
    signal: typeof value.signal === 'string' ? value.signal : null,
    timedOut: value.timedOut === true,
    durationMs:
      typeof value.durationMs === 'number' && Number.isFinite(value.durationMs)
        ? value.durationMs
        : 0,
    output: str(value.output),
    truncated: value.truncated === true,
    ...(typeof value.toolCallId === 'string'
      ? { toolCallId: value.toolCallId }
      : {}),
  };
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * The toolkit's answer when the call never ran: rejected before dispatch
 * (device offline, unknown device, toolkit disabled) or denied, so the tool
 * call carries agent-facing error text instead of an exec result. Null while
 * pending, when an exec result exists, or when there is nothing to show.
 */
export function preDispatchError(
  toolCall?: ApiToolCallModel | null
): string | null {
  if (!toolCall || toolCall.status === 'pending') return null;
  if (parseExecResult(toolCall.result)) return null;
  const text = toolCall.result?.trim() ?? '';
  if (text) return text;
  if (toolCall.status === 'error') return 'The toolkit reported an error.';
  if (toolCall.status === 'denied') return 'The call was denied.';
  return null;
}

export function formatDuration(ms: number): string {
  if (ms < 1_000) return `${Math.max(0, Math.round(ms))}ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(ms < 10_000 ? 1 : 0)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1_000);
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export type ExecOutcome =
  | { kind: 'ok' }
  | { kind: 'failed'; exitCode: number }
  | { kind: 'timedOut' }
  | { kind: 'killed'; signal: string | null };

export function outcomeOf(result: LocalTerminalExecResult): ExecOutcome {
  if (result.timedOut) return { kind: 'timedOut' };
  if (result.exitCode === null)
    return { kind: 'killed', signal: result.signal };
  if (result.exitCode === 0) return { kind: 'ok' };
  return { kind: 'failed', exitCode: result.exitCode };
}

export function describeOutcome(outcome: ExecOutcome): string {
  switch (outcome.kind) {
    case 'ok':
      return 'exit 0';
    case 'failed':
      return `exit ${outcome.exitCode}`;
    case 'timedOut':
      return 'timed out';
    case 'killed':
      return outcome.signal ? `killed (${outcome.signal})` : 'killed';
  }
}

/**
 * Whether a `TERMINAL_RUN` step is still live, judged by its own run rather
 * than the message. The panel marks the last step active until the whole
 * assistant message finishes, but the host reports the command's exit long
 * before that - so the card would show "exit 0" under a "Using Terminal"
 * title. Returns null when there is no run to judge by (fall back to the
 * message-level flag).
 */
export function terminalRunIsActive(
  modality: Modality,
  stepId: string,
  runs: { byStep(stepId: string): { result: unknown } | undefined }
): boolean | null {
  if (!isTerminalRunModality(modality)) return null;
  const run = runs.byStep(stepId);
  return run ? run.result === null : null;
}
